export interface RectCollider {
  kind: 'rect';
  minX: number;
  minZ: number;
  maxX: number;
  maxZ: number;
}

export interface CircleCollider {
  kind: 'circle';
  x: number;
  z: number;
  r: number;
}

export type Collider = RectCollider | CircleCollider;

export interface Point2 {
  x: number;
  z: number;
}

/**
 * 2D collision on the ground plane. Everything that blocks movement is an
 * axis-aligned rectangle or a circle; movers are circles that get pushed out.
 */
export class CollisionWorld {
  private readonly colliders = new Map<number, Collider>();
  private nextId = 1;

  get size(): number {
    return this.colliders.size;
  }

  add(c: Collider): number {
    const id = this.nextId++;
    this.colliders.set(id, c);
    return id;
  }

  /** Rectangle by center and size; `quarterTurns` rotates by 90° steps. */
  addRect(cx: number, cz: number, w: number, d: number, quarterTurns = 0): number {
    const swap = Math.abs(quarterTurns) % 2 === 1;
    const hw = (swap ? d : w) / 2;
    const hd = (swap ? w : d) / 2;
    return this.add({ kind: 'rect', minX: cx - hw, minZ: cz - hd, maxX: cx + hw, maxZ: cz + hd });
  }

  addCircle(x: number, z: number, r: number): number {
    return this.add({ kind: 'circle', x, z, r });
  }

  remove(id: number): void {
    this.colliders.delete(id);
  }

  /** Pushes a circle at `pos` out of every collider. Returns true if it hit anything. */
  resolve(pos: Point2, radius: number, iterations = 2): boolean {
    let hit = false;
    for (let it = 0; it < iterations; it++) {
      let moved = false;
      for (const c of this.colliders.values()) {
        if (pushOut(c, pos, radius)) {
          moved = true;
          hit = true;
        }
      }
      if (!moved) break;
    }
    return hit;
  }

  blocked(x: number, z: number, radius: number): boolean {
    const p = { x, z };
    for (const c of this.colliders.values()) if (overlaps(c, p, radius)) return true;
    return false;
  }
}

function overlaps(c: Collider, p: Point2, r: number): boolean {
  if (c.kind === 'circle') {
    const dx = p.x - c.x;
    const dz = p.z - c.z;
    const rr = r + c.r;
    return dx * dx + dz * dz < rr * rr;
  }
  const cx = Math.max(c.minX, Math.min(p.x, c.maxX));
  const cz = Math.max(c.minZ, Math.min(p.z, c.maxZ));
  const dx = p.x - cx;
  const dz = p.z - cz;
  return dx * dx + dz * dz < r * r;
}

export function pushOut(c: Collider, p: Point2, r: number): boolean {
  if (c.kind === 'circle') {
    const dx = p.x - c.x;
    const dz = p.z - c.z;
    const rr = r + c.r;
    const d2 = dx * dx + dz * dz;
    if (d2 >= rr * rr) return false;
    const d = Math.sqrt(d2);
    if (d < 1e-6) {
      p.x += rr;
      return true;
    }
    const push = rr - d;
    p.x += (dx / d) * push;
    p.z += (dz / d) * push;
    return true;
  }
  // Quick reject.
  if (p.x < c.minX - r || p.x > c.maxX + r || p.z < c.minZ - r || p.z > c.maxZ + r) return false;
  const cx = Math.max(c.minX, Math.min(p.x, c.maxX));
  const cz = Math.max(c.minZ, Math.min(p.z, c.maxZ));
  const dx = p.x - cx;
  const dz = p.z - cz;
  const d2 = dx * dx + dz * dz;
  if (d2 > 1e-9) {
    if (d2 >= r * r) return false;
    const d = Math.sqrt(d2);
    const push = r - d;
    p.x += (dx / d) * push;
    p.z += (dz / d) * push;
    return true;
  }
  // Center is inside the rectangle: exit along the shallowest side.
  const left = p.x - c.minX;
  const right = c.maxX - p.x;
  const top = p.z - c.minZ;
  const bottom = c.maxZ - p.z;
  const m = Math.min(left, right, top, bottom);
  if (m === left) p.x = c.minX - r;
  else if (m === right) p.x = c.maxX + r;
  else if (m === top) p.z = c.minZ - r;
  else p.z = c.maxZ + r;
  return true;
}
